import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Inbox } from "lucide-react";
import { api, API_BASE as API } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
// Note: adapted from bundle (default api + API) to host's named exports — re-apply if upgrading bundle.

function faqJsonLd(faqs) {
  if (!Array.isArray(faqs) || faqs.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

function articleJsonLd(page) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: page.title,
    description: page.meta_description,
    datePublished: page.published_at,
    author: { "@type": "Organization", name: "olyinbox" },
    publisher: { "@type": "Organization", name: "olyinbox" },
    mainEntityOfPage: { "@type": "WebPage", "@id": page.canonical_url },
  };
}

export default function Blog() {
  const { slug } = useParams();
  const [page, setPage] = useState(undefined); // undefined = loading, null = not found

  useEffect(() => {
    let active = true;
    api
      .get(`/marketing/articles/${slug}`)
      .then((r) => {
        if (active) setPage(r.data);
      })
      .catch(() => active && setPage(null));
    return () => {
      active = false;
    };
  }, [slug]);

  useEffect(() => {
    if (!page) return;
    document.title = `${page.title} · olyinbox`;
    const ld = [articleJsonLd(page), faqJsonLd(page.faq)].filter(Boolean);
    const tags = ld.map((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.text = JSON.stringify(obj);
      s.setAttribute("data-blog-ld", "true");
      document.head.appendChild(s);
      return s;
    });
    return () => tags.forEach((t) => t.remove());
  }, [page]);

  if (page === undefined) {
    return (
      <div className="min-h-screen flex flex-col bg-[#FAFAFA]">
        <Header />
        <main className="flex-1 flex items-center justify-center text-[#475569]" data-testid="blog-loading">
          Loading…
        </main>
        <Footer />
      </div>
    );
  }
  if (page === null) {
    return (
      <div className="min-h-screen flex flex-col bg-[#FAFAFA]">
        <Header />
        <main className="flex-1 flex flex-col items-center justify-center gap-3" data-testid="blog-not-found">
          <div className="text-xl font-bold text-[#0F172A]">Article not found</div>
          <Link to="/blog" className="text-[#F26953] hover:underline font-semibold">← Back to all articles</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const ogImage = `${API}/og?id=${page.generated_content_id}&template=${page.angle || "how-to"}`;

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA] text-[#0F172A]" data-testid="blog-page">
      <Header />
      <main className="flex-1">
        <article className="max-w-3xl mx-auto px-6 lg:px-8 py-12 md:py-16">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#F26953] transition-colors mb-6">
            <ArrowLeft className="h-4 w-4" /> All articles
          </Link>
          <header className="mb-10 pb-8 border-b border-[#E2E8F0]">
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#F26953] mb-3">
              {(page.angle || "guide").replace(/-/g, " ")}{page.audience ? ` · for ${page.audience}` : ""}
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight tracking-tight" data-testid="blog-title">
              {page.title}
            </h1>
            {page.meta_description && (
              <p className="mt-5 text-lg text-[#475569] leading-relaxed">{page.meta_description}</p>
            )}
            {page.published_at && (
              <div className="mt-5 text-xs text-slate-400">
                Published {new Date(page.published_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                {page.word_count ? ` · ${page.word_count.toLocaleString()} words` : ""}
              </div>
            )}
            <img src={ogImage} alt={page.title} className="hidden" />
          </header>

          <div
            className="article-body text-[#0F172A]"
            data-testid="blog-body"
            dangerouslySetInnerHTML={{ __html: page.body_html || "" }}
          />

          {Array.isArray(page.faq) && page.faq.length > 0 && (
            <section className="mt-14 border-t border-[#E2E8F0] pt-10" data-testid="blog-faq">
              <h2 className="text-2xl font-bold mb-6 text-[#0F172A]">Frequently asked questions</h2>
              <div className="space-y-6">
                {page.faq.map((f, i) => (
                  <div key={i}>
                    <div className="font-semibold text-[#0F172A] mb-1">{f.q}</div>
                    <div className="text-[#475569] leading-relaxed">{f.a}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {Array.isArray(page.internal_links) && page.internal_links.length > 0 && (
            <section className="mt-14 border-t border-[#E2E8F0] pt-10">
              <h2 className="text-2xl font-bold mb-5 text-[#0F172A]">Keep reading</h2>
              <ul className="space-y-3">
                {page.internal_links.map((l, i) => (
                  <li key={i}>
                    <Link to={`/blog/${l.slug}`} className="text-[#F26953] hover:underline font-semibold">
                      {l.title} →
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="mt-14 p-8 md:p-10 rounded-2xl bg-[#0F172A] text-white text-center" data-testid="blog-cta">
            <Inbox className="h-6 w-6 text-[#F26953] mx-auto mb-3" />
            <div className="text-2xl md:text-3xl font-bold mb-3 tracking-tight">Ready for a professional email?</div>
            <p className="text-slate-300 mb-6 max-w-xl mx-auto text-sm md:text-base">
              Set up email forwarding on your custom domain in 2 minutes. No Google Workspace required.
            </p>
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 bg-[#F26953] hover:bg-[#D85340] text-white font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              Get started — $3/mo <ArrowRight className="h-4 w-4" />
            </Link>
          </section>
        </article>
      </main>
      <Footer />
    </div>
  );
}
